/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { GetTagSemanticiParams } from '../schemas/getTagSemanticiParams.ts';
import type { TagSemanticiInConflittoResponse } from '../schemas/tagSemanticiInConflittoResponse.ts';
import type { TagSemanticoDTO } from '../schemas/tagSemanticoDTO.ts';
import type { TagSemanticoRequest } from '../schemas/tagSemanticoRequest.ts';
import type { WsRispostaPaginataListTagSemanticoDTO } from '../schemas/wsRispostaPaginataListTagSemanticoDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce tag semantico a partire dal suo id
 * @summary Restituisce un tag semantico a partire dal suo id
 */
export const getTagSemanticiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TagSemanticoDTO>(
    { url: `/framework-cfg-utenti/tag-semantici/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetTagSemanticiIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/tag-semantici/${id}`] as const;

export const getGetTagSemanticiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTagSemanticiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTagSemanticiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTagSemanticiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTagSemanticiIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTagSemanticiId>>
  > = ({ signal }) => getTagSemanticiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTagSemanticiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTagSemanticiId>>
>;
export type GetTagSemanticiIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce un tag semantico a partire dal suo id
 */
export const useGetTagSemanticiId = <
  TData = Awaited<ReturnType<typeof getTagSemanticiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTagSemanticiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTagSemanticiIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un tag semantico
 * @summary Aggiorna un tag semantico
 */
export const putTagSemanticiId = (
  id: string,
  tagSemanticoRequest: TagSemanticoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/tag-semantici/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tagSemanticoRequest,
    },
    options,
  );
};

export const getPutTagSemanticiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTagSemanticiId>>,
    TError,
    { id: string; data: TagSemanticoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTagSemanticiId>>,
  TError,
  { id: string; data: TagSemanticoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTagSemanticiId>>,
    { id: string; data: TagSemanticoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTagSemanticiId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTagSemanticiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTagSemanticiId>>
>;
export type PutTagSemanticiIdMutationBody = TagSemanticoRequest;
export type PutTagSemanticiIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un tag semantico
 */
export const usePutTagSemanticiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTagSemanticiId>>,
    TError,
    { id: string; data: TagSemanticoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTagSemanticiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Elimina un tag semantico attraverso il suo id
 * @summary Elimina un tag semantico
 */
export const deleteTagSemanticiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/tag-semantici/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteTagSemanticiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTagSemanticiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTagSemanticiId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTagSemanticiId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTagSemanticiId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTagSemanticiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTagSemanticiId>>
>;

export type DeleteTagSemanticiIdMutationError = ErrorType<unknown>;

/**
 * @summary Elimina un tag semantico
 */
export const useDeleteTagSemanticiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTagSemanticiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTagSemanticiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna un tag semantico
 * @summary Aggiorna un tag semantico per import
 */
export const putTagSemanticiIdImport = (
  id: string,
  tagSemanticoDTO: TagSemanticoDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/tag-semantici/${id}/import`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tagSemanticoDTO,
    },
    options,
  );
};

export const getPutTagSemanticiIdImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTagSemanticiIdImport>>,
    TError,
    { id: string; data: TagSemanticoDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTagSemanticiIdImport>>,
  TError,
  { id: string; data: TagSemanticoDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTagSemanticiIdImport>>,
    { id: string; data: TagSemanticoDTO }
  > = props => {
    const { id, data } = props ?? {};

    return putTagSemanticiIdImport(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTagSemanticiIdImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTagSemanticiIdImport>>
>;
export type PutTagSemanticiIdImportMutationBody = TagSemanticoDTO;
export type PutTagSemanticiIdImportMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un tag semantico per import
 */
export const usePutTagSemanticiIdImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTagSemanticiIdImport>>,
    TError,
    { id: string; data: TagSemanticoDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTagSemanticiIdImportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di tag semantici. Ordinabili per nome, tipo
 * @summary Cerca una serie di tag semantici. Ordinabili per nome, tipo
 */
export const getTagSemantici = (
  params?: GetTagSemanticiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTagSemanticoDTO>(
    {
      url: `/framework-cfg-utenti/tag-semantici`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTagSemanticiQueryKey = (params?: GetTagSemanticiParams) =>
  [`/framework-cfg-utenti/tag-semantici`, ...(params ? [params] : [])] as const;

export const getGetTagSemanticiQueryOptions = <
  TData = Awaited<ReturnType<typeof getTagSemantici>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTagSemanticiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTagSemantici>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTagSemantici>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTagSemanticiQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTagSemantici>>> = ({
    signal,
  }) => getTagSemantici(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTagSemanticiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTagSemantici>>
>;
export type GetTagSemanticiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di tag semantici. Ordinabili per nome, tipo
 */
export const useGetTagSemantici = <
  TData = Awaited<ReturnType<typeof getTagSemantici>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTagSemanticiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTagSemantici>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTagSemanticiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un tag semantico
 * @summary Crea un tag semantico
 */
export const postTagSemantici = (
  tagSemanticoRequest: TagSemanticoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/tag-semantici`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tagSemanticoRequest,
    },
    options,
  );
};

export const getPostTagSemanticiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemantici>>,
    TError,
    { data: TagSemanticoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTagSemantici>>,
  TError,
  { data: TagSemanticoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTagSemantici>>,
    { data: TagSemanticoRequest }
  > = props => {
    const { data } = props ?? {};

    return postTagSemantici(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTagSemanticiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTagSemantici>>
>;
export type PostTagSemanticiMutationBody = TagSemanticoRequest;
export type PostTagSemanticiMutationError = ErrorType<unknown>;

/**
 * @summary Crea un tag semantico
 */
export const usePostTagSemantici = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemantici>>,
    TError,
    { data: TagSemanticoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTagSemanticiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea un tag semantico
 * @summary Crea un tag semantico per import
 */
export const postTagSemanticiImport = (
  tagSemanticoDTO: TagSemanticoDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/tag-semantici/import`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tagSemanticoDTO,
    },
    options,
  );
};

export const getPostTagSemanticiImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiImport>>,
    TError,
    { data: TagSemanticoDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTagSemanticiImport>>,
  TError,
  { data: TagSemanticoDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTagSemanticiImport>>,
    { data: TagSemanticoDTO }
  > = props => {
    const { data } = props ?? {};

    return postTagSemanticiImport(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTagSemanticiImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTagSemanticiImport>>
>;
export type PostTagSemanticiImportMutationBody = TagSemanticoDTO;
export type PostTagSemanticiImportMutationError = ErrorType<unknown>;

/**
 * @summary Crea un tag semantico per import
 */
export const usePostTagSemanticiImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiImport>>,
    TError,
    { data: TagSemanticoDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTagSemanticiImportMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postTagSemanticiConflitti = (
  tagSemanticoDTO: TagSemanticoDTO[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TagSemanticiInConflittoResponse>(
    {
      url: `/framework-cfg-utenti/tag-semantici/conflitti`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tagSemanticoDTO,
    },
    options,
  );
};

export const getPostTagSemanticiConflittiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiConflitti>>,
    TError,
    { data: TagSemanticoDTO[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTagSemanticiConflitti>>,
  TError,
  { data: TagSemanticoDTO[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTagSemanticiConflitti>>,
    { data: TagSemanticoDTO[] }
  > = props => {
    const { data } = props ?? {};

    return postTagSemanticiConflitti(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTagSemanticiConflittiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTagSemanticiConflitti>>
>;
export type PostTagSemanticiConflittiMutationBody = TagSemanticoDTO[];
export type PostTagSemanticiConflittiMutationError = ErrorType<unknown>;

export const usePostTagSemanticiConflitti = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiConflitti>>,
    TError,
    { data: TagSemanticoDTO[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTagSemanticiConflittiMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postTagSemanticiB2B = (
  postTagSemanticiB2BBody: string[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TagSemanticoDTO[]>(
    {
      url: `/framework-cfg-utenti/tag-semantici/b2b`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: postTagSemanticiB2BBody,
    },
    options,
  );
};

export const getPostTagSemanticiB2BMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiB2B>>,
    TError,
    { data: string[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTagSemanticiB2B>>,
  TError,
  { data: string[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTagSemanticiB2B>>,
    { data: string[] }
  > = props => {
    const { data } = props ?? {};

    return postTagSemanticiB2B(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTagSemanticiB2BMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTagSemanticiB2B>>
>;
export type PostTagSemanticiB2BMutationBody = string[];
export type PostTagSemanticiB2BMutationError = ErrorType<unknown>;

export const usePostTagSemanticiB2B = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTagSemanticiB2B>>,
    TError,
    { data: string[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTagSemanticiB2BMutationOptions(options);

  return useMutation(mutationOptions);
};
